/**
 * FeaturesPage - Production Features Page
 *
 * Based on Features_BentoA (the winner).
 * Live at /features route.
 *
 * i18n: arrays hold catalog keys (features_page.*), resolved via t() at
 * render. Theme names are proper nouns and stay English (scope law).
 */

import { Link } from 'react-router-dom';
import { useAuth } from '@services/auth';
import { useLanguage } from '@/i18n';
import { PublicLanguageSelect } from '@/components/PublicLanguageSelect';
import './Features_BentoA.css'; // Reuse the same styles

const HERO_FEATURES = [
  { icon: 'cloud_off', label: 'features_page.hero_offline', desc: 'features_page.hero_offline_desc' },
  { icon: 'devices', label: 'features_page.hero_devices', desc: 'features_page.hero_devices_desc' },
  { icon: 'palette', label: 'features_page.hero_themes', desc: 'features_page.hero_themes_desc' },
  { icon: 'lock', label: 'features_page.hero_privacy', desc: 'features_page.hero_privacy_desc' },
];

const MAIN_FEATURES = [
  {
    id: 'offline',
    size: 'large',
    title: 'features_page.offline_title',
    subtitle: 'features_page.offline_sub',
    bullets: [
      'features_page.offline_b1',
      'features_page.offline_b2',
      'features_page.offline_b3',
      'features_page.offline_b4',
    ],
    accent: '#7aa2f7',
    icon: 'wifi_off',
  },
  {
    id: 'sync',
    size: 'medium',
    title: 'features_page.sync_title',
    subtitle: 'features_page.sync_sub',
    bullets: [
      'features_page.sync_b1',
      'features_page.sync_b2',
      'features_page.sync_b3',
    ],
    accent: '#bb9af7',
    icon: 'sync',
  },
  {
    id: 'analytics',
    size: 'medium',
    title: 'features_page.analytics_title',
    subtitle: 'features_page.analytics_sub',
    bullets: [
      'features_page.analytics_b1',
      'features_page.analytics_b2',
      'features_page.analytics_b3',
    ],
    accent: '#9ece6a',
    icon: 'insights',
  },
  {
    id: 'transactions',
    size: 'small',
    title: 'features_page.tx_title',
    subtitle: 'features_page.tx_sub',
    accent: '#7dcfff',
    icon: 'add_circle',
  },
  {
    id: 'categories',
    size: 'small',
    title: 'features_page.cat_title',
    subtitle: 'features_page.cat_sub',
    accent: '#e0af68',
    icon: 'category',
  },
  {
    id: 'import',
    size: 'small',
    title: 'features_page.import_title',
    subtitle: 'features_page.import_sub',
    accent: '#f7768e',
    icon: 'upload_file',
  },
  {
    id: 'privacy',
    size: 'wide',
    title: 'features_page.privacy_title',
    subtitle: 'features_page.privacy_sub',
    bullets: [
      'features_page.privacy_b1',
      'features_page.privacy_b2',
      'features_page.privacy_b3',
      'features_page.privacy_b4',
    ],
    accent: '#c0caf5',
    icon: 'shield',
  },
  {
    id: 'themes',
    size: 'medium',
    title: 'features_page.themes_title',
    subtitle: 'features_page.themes_sub',
    // Theme names are product proper nouns - not translated
    themes: ['Tokyo Night', 'Midnight Blue', 'Emerald Dark', 'Rose Dark', 'Amber Dark', 'Ninja Stealth', 'Ocean Depths', 'Sunset Glow', 'Lavender Mist'],
    accent: '#bb9af7',
    icon: 'palette',
  },
  {
    id: 'recurring',
    size: 'medium',
    title: 'features_page.recurring_title',
    subtitle: 'features_page.recurring_sub',
    bullets: [
      'features_page.recurring_b1',
      'features_page.recurring_b2',
      'features_page.recurring_b3',
    ],
    accent: '#7aa2f7',
    icon: 'event_repeat',
  },
  {
    id: 'pwa',
    size: 'wide',
    title: 'features_page.pwa_title',
    subtitle: 'features_page.pwa_sub',
    bullets: [
      'features_page.pwa_b1',
      'features_page.pwa_b2',
      'features_page.pwa_b3',
      'features_page.pwa_b4',
    ],
    accent: '#7dcfff',
    icon: 'install_mobile',
  },
];

// Prices stay literal ($ pricing is the product's actual pricing)
const PRICING = [
  { tier: 'features_page.tier_free', price: '$0', features: ['features_page.pf_full_app', 'features_page.pf_single_device', 'features_page.pf_all_themes', 'features_page.pf_import_export'] },
  { tier: 'features_page.tier_pro', price: '$4.99/mo', features: ['features_page.pf_everything_free', 'features_page.pf_sync', 'features_page.pf_backup', 'features_page.pf_trial'] },
  { tier: 'features_page.tier_lifetime', price: '$49', features: ['features_page.pf_pro_forever', 'features_page.pf_one_time', 'features_page.pf_limited'] },
];

export default function FeaturesPage() {
  const { signInWithGoogle, isLoading } = useAuth();
  const { t } = useLanguage();

  const handleGetStarted = async () => {
    await signInWithGoogle();
  };

  return (
    <div className="feat-bento">
      {/* Header */}
      <header className="feat-bento-header">
        <Link to="/" className="feat-bento-logo">NinjaCash</Link>
        <nav className="feat-bento-nav">
          <PublicLanguageSelect />
          <Link to="/" className="feat-bento-nav-link">{t('features_page.nav_home')}</Link>
        </nav>
      </header>

      {/* Hero */}
      <section className="feat-bento-hero">
        <h1 className="feat-bento-title">
          <span className="feat-bento-title-line">{t('features_page.title_line')}</span>
          <span className="feat-bento-title-accent">{t('features_page.title_accent')}</span>
        </h1>
        <p className="feat-bento-tagline">
          {t('features_page.tagline')}
        </p>
        <div className="feat-bento-hero-stats">
          {HERO_FEATURES.map((f) => (
            <div key={f.label} className="feat-bento-stat">
              <span className="material-symbols-outlined feat-bento-stat-icon">{f.icon}</span>
              <span className="feat-bento-stat-label">{t(f.label)}</span>
              <span className="feat-bento-stat-desc">{t(f.desc)}</span>
            </div>
          ))}
        </div>
      </section>

      {/* Bento Grid */}
      <section className="feat-bento-grid">
        {MAIN_FEATURES.map((feat) => (
          <article
            key={feat.id}
            className={`feat-bento-card feat-bento-card--${feat.size}`}
            style={{ '--card-accent': feat.accent } as React.CSSProperties}
          >
            <div className="feat-bento-card-header">
              <span className="material-symbols-outlined feat-bento-card-icon">{feat.icon}</span>
              <div className="feat-bento-card-titles">
                <h3 className="feat-bento-card-title">{t(feat.title)}</h3>
                <p className="feat-bento-card-subtitle">{t(feat.subtitle)}</p>
              </div>
            </div>
            {feat.bullets && (
              <ul className="feat-bento-card-bullets">
                {feat.bullets.map((b) => (
                  <li key={b}>{t(b)}</li>
                ))}
              </ul>
            )}
            {feat.themes && (
              <div className="feat-bento-themes">
                {feat.themes.map((name) => (
                  <span key={name} className="feat-bento-theme-tag">{name}</span>
                ))}
              </div>
            )}
          </article>
        ))}
      </section>

      {/* Pricing Preview */}
      <section className="feat-bento-pricing">
        <h2 className="feat-bento-section-title">{t('features_page.pricing_title')}</h2>
        <div className="feat-bento-pricing-grid">
          {PRICING.map((p) => (
            <div key={p.tier} className={`feat-bento-price-card ${p.tier === 'features_page.tier_pro' ? 'feat-bento-price-card--featured' : ''}`}>
              <h3 className="feat-bento-price-tier">{t(p.tier)}</h3>
              <p className="feat-bento-price-amount">{p.price}</p>
              <ul className="feat-bento-price-features">
                {p.features.map((f) => (
                  <li key={f}>{t(f)}</li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="feat-bento-cta">
        <h2 className="feat-bento-cta-title">{t('features_page.cta_title')}</h2>
        <p className="feat-bento-cta-desc">{t('features_page.cta_desc')}</p>
        <button
          className="feat-bento-cta-btn"
          onClick={handleGetStarted}
          disabled={isLoading}
        >
          {isLoading ? t('common.loading') : t('features_page.cta_btn')}
        </button>
      </section>

      {/* Footer */}
      <footer className="feat-bento-footer">
        <div className="feat-bento-footer-links">
          <Link to="/privacy">{t('settings.legal.privacy')}</Link>
          <Link to="/terms">{t('settings.legal.terms')}</Link>
          <a href="https://github.com/TakashiKyoto/ninjacash" target="_blank" rel="noopener noreferrer">GitHub</a>
        </div>
        <p className="feat-bento-footer-copy">&copy; 2026 NinjaCash. {t('features_page.footer_copy')}</p>
      </footer>
    </div>
  );
}
